import { useMemo, useState, type ReactNode } from 'react';
import { DndContext, DragOverlay, PointerSensor, KeyboardSensor, closestCorners, useDroppable, useSensor, useSensors, type DragEndEvent, type DragStartEvent } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { FieldMeta, ResourceMeta } from '../meta/types';
import { renderField, type Widgets } from '../fields/registry';
import { Badge, EmptyState, type Tone } from '../design/primitives/Misc';
import { Icon } from '../design/icons';

// Board (kanban): columns from an enum or relation field; dragging a card between columns
// calls onMove(row, newValue) — the screen turns that into a PATCH. WIP counts per column
// and optional WIP limits.

export interface BoardColumn {
  value: string;
  label: string;
  tone?: Tone;
  /** WIP limit; the header turns warn when exceeded. */
  wip?: number;
}

export interface BoardProps<T = Record<string, unknown>> {
  meta?: ResourceMeta;
  rows: T[];
  keyOf: (row: T) => string;
  /** The grouping field. */
  field: FieldMeta;
  /** Column definitions; derived from `field.enum` when omitted. */
  columns?: BoardColumn[];
  labelOf: (row: T) => ReactNode;
  /** Extra fields rendered on the card (below the label). */
  cardFields?: FieldMeta[];
  card?: (row: T) => ReactNode;
  onMove?: (row: T, value: string, previous: string | null) => void | Promise<void>;
  onOpen?: (row: T) => void;
  widgets?: Widgets;
  loading?: boolean;
  empty?: ReactNode;
  wip?: Record<string, number>;
}

function Card<T>({ id, row, children, onOpen }: { id: string; row: T; children: ReactNode; onOpen?: (row: T) => void }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  return (
    <div
      ref={setNodeRef}
      className={['hf-card-k', isDragging && 'dragging'].filter(Boolean).join(' ')}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      {...attributes}
      {...listeners}
      onClick={() => onOpen?.(row)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && onOpen) onOpen(row);
      }}
    >
      {children}
    </div>
  );
}

function Column({ id, children, count, label, tone, wip }: { id: string; children: ReactNode; count: number; label: string; tone?: Tone; wip?: number }) {
  const { setNodeRef, isOver } = useDroppable({ id: `col:${id}` });
  const over = wip != null && count > wip;
  return (
    <section ref={setNodeRef} className={['hf-board-col', isOver && 'over'].filter(Boolean).join(' ')} aria-label={label}>
      <header className="hf-board-head">
        <Badge tone={tone ?? 'muted'}>{label}</Badge>
        <span className={['hf-num', 'count', over && 'over'].filter(Boolean).join(' ')} title={wip != null ? `WIP limit ${wip}` : undefined}>
          {count}
          {wip != null && <span className="hf-muted">/{wip}</span>}
        </span>
      </header>
      <div className="hf-board-cards hf-scroll">{children}</div>
    </section>
  );
}

export function Board<T = Record<string, unknown>>({ rows, keyOf, field, columns, labelOf, cardFields = [], card, onMove, onOpen, widgets, empty, wip }: BoardProps<T>) {
  const cols = useMemo<BoardColumn[]>(() => {
    if (columns) return columns;
    const fromEnum = (field.enum ?? []).map((o) => ({ value: o.value, label: o.label, tone: o.tone as Tone | undefined, wip: wip?.[o.value] }));
    if (fromEnum.length) return fromEnum;
    // Derive from data (relation or free text).
    const seen = new Map<string, string>();
    for (const r of rows) {
      const v = (r as Record<string, unknown>)[field.name];
      if (v != null) seen.set(String(v), String((r as Record<string, unknown>)[`${field.name.replace(/_id$/, '')}_name`] ?? v));
    }
    return [...seen].map(([value, label]) => ({ value, label, wip: wip?.[value] }));
  }, [columns, field, rows, wip]);

  const byCol = useMemo(() => {
    const m = new Map<string, T[]>();
    for (const c of cols) m.set(c.value, []);
    const other: T[] = [];
    for (const r of rows) {
      const v = (r as Record<string, unknown>)[field.name];
      const k = v == null ? '' : String(v);
      if (m.has(k)) m.get(k)!.push(r);
      else other.push(r);
    }
    return { m, other };
  }, [rows, cols, field.name]);

  const [activeId, setActiveId] = useState<string | null>(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
  const rowById = useMemo(() => new Map(rows.map((r) => [keyOf(r), r])), [rows, keyOf]);

  const onDragEnd = (e: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = e;
    if (!over) return;
    const row = rowById.get(String(active.id));
    if (!row) return;
    let target: string | null = null;
    const overId = String(over.id);
    if (overId.startsWith('col:')) target = overId.slice(4);
    else {
      const overRow = rowById.get(overId);
      if (overRow) target = String((overRow as Record<string, unknown>)[field.name] ?? '');
    }
    const prev = (row as Record<string, unknown>)[field.name];
    const previous = prev == null ? null : String(prev);
    if (target != null && target !== previous) void onMove?.(row, target, previous);
  };

  if (rows.length === 0 && !empty) return <EmptyState title="Nothing to show" icon="kanban" />;
  if (rows.length === 0) return <>{empty}</>;

  const renderCard = (r: T) => (
    <>
      <div className="title">{labelOf(r)}</div>
      {card ? (
        card(r)
      ) : cardFields.length ? (
        <div className="meta">
          {cardFields.map((f) => (
            <span key={f.name} className="pair">
              <span className="hf-muted">{f.label}</span> {renderField(f, (r as Record<string, unknown>)[f.name], r as Record<string, unknown>, { compact: true, widgets })}
            </span>
          ))}
        </div>
      ) : null}
    </>
  );

  const activeRow = activeId ? rowById.get(activeId) : undefined;

  return (
    <DndContext sensors={sensors} collisionDetection={closestCorners} onDragStart={(e: DragStartEvent) => setActiveId(String(e.active.id))} onDragEnd={onDragEnd} onDragCancel={() => setActiveId(null)}>
      <div className="hf-board hf-scroll" role="list" aria-label={`Board by ${field.label}`}>
        {cols.map((c) => {
          const list = byCol.m.get(c.value) ?? [];
          return (
            <Column key={c.value} id={c.value} label={c.label} tone={c.tone} count={list.length} wip={c.wip}>
              <SortableContext items={list.map(keyOf)} strategy={verticalListSortingStrategy}>
                {list.map((r) => (
                  <Card key={keyOf(r)} id={keyOf(r)} row={r} onOpen={onOpen}>
                    {renderCard(r)}
                  </Card>
                ))}
              </SortableContext>
              {list.length === 0 && (
                <div className="hf-board-empty">
                  <Icon name="plus" size={12} /> Drop here
                </div>
              )}
            </Column>
          );
        })}
        {byCol.other.length > 0 && (
          <Column id="" label="Unassigned" count={byCol.other.length}>
            <SortableContext items={byCol.other.map(keyOf)} strategy={verticalListSortingStrategy}>
              {byCol.other.map((r) => (
                <Card key={keyOf(r)} id={keyOf(r)} row={r} onOpen={onOpen}>
                  {renderCard(r)}
                </Card>
              ))}
            </SortableContext>
          </Column>
        )}
      </div>
      <DragOverlay>{activeRow ? <div className="hf-card-k overlay">{renderCard(activeRow)}</div> : null}</DragOverlay>
    </DndContext>
  );
}
